// src/pages/Admin_Module.jsx
import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams, Link } from "react-router-dom";
import axios from "axios";
import Swal from "sweetalert2";
import SuperAdminNav from "../components/superAdminNav";
import Time from "../components/time";

import {
  ArrowLeft,
  BadgeCheck,
  Boxes,
  Building2,
  Save as SaveIcon,
  User as UserIcon,
} from "lucide-react";

const API = "https://superadmin.nissiindia.com";

const MODULE_CATALOG = [
  { label: "Dashboard", link: "/dashboard", id: 1, category: "Main Site Modules" },
  { label: "User Details", link: "/user-details", id: 2, category: "Main Site Modules" },
  { label: "Route Management", link: "/route-management", id: 3, category: "Main Site Modules" },
  { label: "Installation Module", link: "/installation", id: 4, category: "Main Site Modules" },
  { label: "Reporting System", link: "/reporting-system", id: 5, category: "Main Site Modules" },
  { label: "Sentinel Reports", link: "/sentinel_reports", id: 6, category: "Main Site Modules" },
  { label: "InCampus Module", link: "/incampus", id: 7, category: "Main Site Modules" },
  { label: "QR Codes", link: "/qr-codes", id: 8, category: "Main Site Modules" },

  { label: "User List", link: "/user_list", id: 101, category: "User Details" },
  { label: "Add New User", link: "/add_user", id: 102, category: "User Details" },
  { label: "Sentinel User List", link: "/sentinel_user_list", id: 103, category: "User Details" },
  { label: "User Incident Types", link: "/add_incidents", id: 104, category: "User Details" },
  { label: "Registered Devices", link: "/registered_devices", id: 105, category: "User Details" },

  { label: "Create Route", link: "/create_route", id: 201, category: "Route Management" },
  { label: "Route List", link: "/route_list", id: 202, category: "Route Management" },
  { label: "Sentinel Route List", link: "/sentinel_route_list", id: 203, category: "Route Management" },
  { label: "Escort List", link: "/escort_list", id: 204, category: "Route Management" },
  { label: "Create Escort", link: "/create_escort", id: 205, category: "Route Management" },
  { label: "Sentinel Escort List", link: "/sentinel_escort_list", id: 206, category: "Route Management" },
  { label: "Sentinel Add Escort", link: "/sentinel_add_escort", id: 207, category: "Route Management" },

  { label: "Patrol QR", link: "/patrol-qr", id: 301, category: "QR Codes" },
  { label: "Sentinel QR", link: "/sentinel-qr", id: 302, category: "QR Codes" },

  { label: "Analytics Module", link: "/analytics", id: 401, category: "Reporting System" },
  { label: "Summaries Module", link: "/summaries", id: 402, category: "Reporting System" },
  { label: "Export Module", link: "/export", id: 403, category: "Reporting System" },
  { label: "Custom Module", link: "/custom", id: 404, category: "Reporting System" },

  { label: "InCampus Modules", link: "/incampus_modules", id: 501, category: "InCampus Module" },
  { label: "InCampus List", link: "/incampus_list", id: 502, category: "InCampus Module" },

  // Sentinel Mobile App Pages
{ label: "App Dashboard", link: "Dashboard", id: 1001, category: "Sentinel App Pages" },
{ label: "App Route List", link: "RouteList", id: 1002, category: "Sentinel App Pages" },
{ label: "GPS Patrol", link: "GPS", id: 1003, category: "Sentinel App Pages" },
{ label: "QR Scanning", link: "Scanning", id: 1004, category: "Sentinel App Pages" },
{ label: "Incidents", link: "Incidents", id: 1005, category: "Sentinel App Pages" },
{ label: "Campus Check", link: "CampusCheck", id: 1006, category: "Sentinel App Pages" },
{ label: "Escort", link: "Escort", id: 1007, category: "Sentinel App Pages" },
{ label: "Emergency", link: "Emergencies", id: 1008, category: "Sentinel App Pages" },
{ label: "Selfie Verification", link: "SelfieVerification", id: 1009, category: "Sentinel App Pages" },
];

function useTenantId() {
  const q = new URLSearchParams(useLocation().search);
  return q.get("tid");
}

export default function Admin_Module() {
  const { username: routeUsername } = useParams();
  const tenantId = useTenantId();
  const username = decodeURIComponent(routeUsername || "");
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [tenant, setTenant] = useState(null);
  const [checked, setChecked] = useState({});

  const groupedModules = useMemo(() => {
    return MODULE_CATALOG.reduce((acc, item) => {
      if (!acc[item.category]) acc[item.category] = [];
      acc[item.category].push(item);
      return acc;
    }, {});
  }, []);

  const allChecked = useMemo(
    () =>
      MODULE_CATALOG.length > 0 &&
      MODULE_CATALOG.every((m) => checked[m.link]),
    [checked]
  );

  useEffect(() => {
    (async () => {
      if (!tenantId || !username) return;

      setLoading(true);

      try {
        const [t, g] = await Promise.all([
          axios.get(`${API}/api/tenant/basic/${tenantId}`),
          axios.get(
            `${API}/api/tenant/mysql/modules/${tenantId}/${encodeURIComponent(
              username
            )}`
          ),
        ]);

        setTenant(t.data?.tenant || null);

        const grants = g.data?.modules || [];
        const map = {};

        grants.forEach((row) => {
          if (row.approve) {
            map[row.links] = true;
          }
        });

        setChecked(map);
      } catch (e) {
        Swal.fire("Error", e?.response?.data?.error || e.message, "error");
      } finally {
        setLoading(false);
      }
    })();
  }, [tenantId, username]);

  const toggleAll = () => {
    const next = {};

    if (!allChecked) {
      MODULE_CATALOG.forEach((m) => {
        next[m.link] = true;
      });
    }

    setChecked(next);
  };

  const toggleOne = (link) => {
    setChecked((prev) => ({
      ...prev,
      [link]: !prev[link],
    }));
  };

  const toggleCategory = (items) => {
    const categoryAllChecked = items.every((m) => checked[m.link]);

    setChecked((prev) => {
      const next = { ...prev };

      items.forEach((m) => {
        next[m.link] = !categoryAllChecked;
      });

      return next;
    });
  };

  const onSave = async (e) => {
    e.preventDefault();

    if (!tenantId || !username) return;

    const selected = MODULE_CATALOG.filter((m) => checked[m.link]).map((m) => ({
      pg_id: m.id,
      modules: m.label,
      links: m.link,
      approve: 1,
    }));

    setSaving(true);

    try {
      await axios.put(
        `${API}/api/tenant/mysql/modules/${tenantId}/${encodeURIComponent(
          username
        )}`,
        { modules: selected }
      );

      Swal.fire("Saved", "Module access updated.", "success");
      navigate("/superAdmin/admin_Menu");
    } catch (e) {
      Swal.fire("Error", e?.response?.data?.error || e.message, "error");
    } finally {
      setSaving(false);
    }
  };

  const renderModuleCard = (m) => (
    <label
      key={`${m.id}-${m.link}`}
      className="flex items-center gap-3 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 hover:bg-slate-100"
    >
      <input
        type="checkbox"
        className="h-4 w-4"
        checked={!!checked[m.link]}
        onChange={() => toggleOne(m.link)}
        disabled={loading || saving}
      />

      <div>
        <div className="text-sm font-medium text-slate-800">{m.label}</div>
        <div className="text-xs text-slate-500">
          ID: {m.id} • {m.link}
        </div>
      </div>
    </label>
  );

  return (
    <div className="min-h-screen bg-slate-100">
      <div className="flex flex-col md:flex-row md:h-screen md:overflow-hidden">
        <SuperAdminNav />

        <main className="w-full md:w-3/4 md:h-full md:overflow-auto">
          <div className="bg-gradient-to-r from-indigo-600 via-blue-600 to-cyan-500 text-white">
            <div className="px-4 sm:px-6 lg:px-8 py-5">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-white/15 ring-1 ring-white/30">
                    <Boxes className="h-5 w-5" />
                  </div>

                  <div>
                    <h1 className="text-2xl font-semibold leading-tight">
                      Assign Site Modules
                    </h1>
                    <p className="text-white/80 text-sm">
                      Enable/Disable site modules and submenu access
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <Link
                    to="/superAdmin/admin_Menu"
                    className="inline-flex items-center gap-2 rounded-lg bg-white/15 px-3 py-1.5 text-white ring-1 ring-white/30 hover:bg-white/20"
                  >
                    <ArrowLeft className="h-4 w-4" />
                    Back
                  </Link>

                  <div className="text-sm text-white/90">
                    <Time />
                  </div>
                </div>
              </div>

              <div className="mt-4 flex flex-wrap items-center gap-2">
                <span className="inline-flex items-center gap-2 rounded-full bg-white/15 px-3 py-1 text-xs ring-1 ring-white/30">
                  <Building2 className="h-4 w-4" />
                  {tenant?.tenantCode || "Tenant"} —{" "}
                  {tenant?.databaseName || "-"}
                </span>

                <span className="inline-flex items-center gap-2 rounded-full bg-white/15 px-3 py-1 text-xs ring-1 ring-white/30">
                  <UserIcon className="h-4 w-4" />
                  {username}
                </span>

                <span className="inline-flex items-center gap-2 rounded-full bg-white/15 px-3 py-1 text-xs ring-1 ring-white/30">
                  <BadgeCheck className="h-4 w-4" />
                  Select to grant access
                </span>
              </div>
            </div>
          </div>

          <div className="px-4 sm:px-6 lg:px-8 py-6">
            <form
              onSubmit={onSave}
              className="rounded-2xl border border-slate-200 bg-white shadow-sm"
            >
              <div className="flex items-center justify-between border-b border-slate-200 bg-gradient-to-r from-cyan-50 to-teal-50 px-4 py-3 rounded-t-2xl">
                <div className="flex items-center gap-2">
                  <Boxes className="h-4 w-4 text-cyan-600" />
                  <h3 className="text-sm font-semibold text-slate-800">
                    Site Module Access
                  </h3>
                </div>

                <button
                  type="button"
                  onClick={toggleAll}
                  className="rounded-lg bg-slate-900 text-white px-3 py-1.5 text-sm shadow hover:bg-black disabled:opacity-60"
                  disabled={loading || saving}
                >
                  {allChecked ? "Unselect All" : "Select All"}
                </button>
              </div>

              <div className="p-4 space-y-6">
                {Object.entries(groupedModules).map(([category, items]) => {
                  const categoryAllChecked = items.every(
                    (m) => checked[m.link]
                  );

                  return (
                    <section
                      key={category}
                      className="rounded-xl border border-slate-200 bg-white"
                    >
                      <div className="flex items-center justify-between border-b border-slate-100 bg-slate-50 px-4 py-3 rounded-t-xl">
                        <div>
                          <h4 className="text-sm font-semibold text-slate-900">
                            {category}
                          </h4>
                          <p className="text-xs text-slate-500">
                            {items.length} module{items.length > 1 ? "s" : ""}
                          </p>
                        </div>

                        <button
                          type="button"
                          onClick={() => toggleCategory(items)}
                          disabled={loading || saving}
                          className="rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs text-slate-800 shadow-sm hover:bg-slate-100 disabled:opacity-60"
                        >
                          {categoryAllChecked
                            ? "Unselect Section"
                            : "Select Section"}
                        </button>
                      </div>

                      <div className="p-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                        {items.map(renderModuleCard)}
                      </div>
                    </section>
                  );
                })}
              </div>

              <div className="flex items-center justify-end gap-2 px-4 py-4 border-t border-slate-200 rounded-b-2xl bg-white/60">
                <button
                  type="submit"
                  className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-white shadow hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500/30 disabled:opacity-60"
                  disabled={saving || loading}
                >
                  <SaveIcon className="h-4 w-4" />
                  {saving ? "Saving…" : "Save Modules"}
                </button>
              </div>
            </form>
          </div>
        </main>
      </div>
    </div>
  );
}